import { Levels } from './levels.js';
import type { FeatureControl } from './world.js';

/**
 * A world feature with named states (ADR-032, "World features and states"),
 * as levels of its effects: the volcano's plume and eruption, the burner's
 * flame. Forward by one step eases to the new state over `seconds`; anything
 * else is there at once; a step that keeps the state changes nothing, so an
 * eruption does not start over at the quiz after it. Every world's features
 * are built on this; the effects only read the levels.
 */
export function stateFeature<K extends string>(options: {
  states: Readonly<Record<string, Record<K, number>>>;
  initial: string;
  /** How long a change takes when the class moves on by one step; 3 s by default. */
  seconds?: number;
  /** After the levels were set: for what a level alone cannot say (a flow starting over). */
  onChange?: (state: string, animate: boolean) => void;
}): {
  levels: Levels<K>;
  control: FeatureControl;
  readonly state: string;
  update(dt: number): void;
} {
  const fallback = options.states[options.initial];
  if (!fallback) throw new Error(`no state "${options.initial}"`);
  const levels = new Levels<K>(fallback);
  let state = options.initial;
  return {
    levels,
    get state() {
      return state;
    },
    control: {
      set(next, animate) {
        const known = options.states[next] ? next : options.initial;
        if (known === state) return;
        state = known;
        levels.set(options.states[known]!, animate ? (options.seconds ?? 3) : 0);
        options.onChange?.(known, animate);
      },
    },
    update(dt) {
      levels.update(dt);
    },
  };
}
